import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { getErrorMessage } from "@/utils/withErrorHandling";
import {
  CaptureMatch,
  CaptureRule,
  PushCapture,
  PushReject,
  PushStationCredential,
  PushStationService,
} from "@/services/PushStationService";
import { paginate, sortRows, SortAccessors, useTableState } from "@/hooks/useTableState";
import Pagination from "@/components/common/Pagination";
import SortableHeader from "@/components/common/SortableHeader";
import FilterSelect from "@/components/common/FilterSelect";
import SearchInput from "@/components/common/SearchInput";
import TableSkeleton from "@/components/common/TableSkeleton";
import ActionsMenu from "@/components/common/ActionsMenu";
import ConfirmDialog from "@/components/common/ConfirmDialog";

/**
 * Rejected-push inspector (Manage stations → "Rejected pushes").
 *
 * Shows which credentials the live-wind edge is REJECTING (aggregated per protocol / path /
 * token hash / IP over 7 days), lets an admin capture the next few rejected requests from one
 * prefix or IP to read the token a device actually sends, and adopt that token as a station's
 * credential. Built for the case where a device keeps pushing a credential we no longer have
 * and its owner cannot be reached.
 *
 * Secrets: the aggregate carries only the public 10-char prefix. Captures come back MASKED;
 * "Reveal" fetches one capture unmasked (the backend audits it) and the raw value stays in
 * this component's state only until the panel closes.
 */

/** Param namespace — this table shares the URL with the station table on the same page. */
export const REJECTS_PARAM_PREFIX = "rj_";

const PAGE_SIZES = [25, 50, 100];
const FILTERS = { protocol: "all" };
type Filters = typeof FILTERS;

const ACCESSORS: SortAccessors<PushReject> = {
  lastSeen: (r) => r.lastSeen,
  protocol: (r) => r.protocol,
  path: (r) => r.path,
  prefix: (r) => r.tokenPrefix,
  ip: (r) => r.ip,
  ua: (r) => r.userAgent,
  count: (r) => r.count,
  hint: (r) => stationHintOf(r),
};

export const stationHintOf = (r: Pick<PushReject, "hintWuId" | "hintWindguruUid">): string | null =>
  r.hintWuId || r.hintWindguruUid || null;

/**
 * Could this reject belong to `station`? A WU `ID` / Windguru `uid` naming the station, or a
 * rejected token that still carries the station's current prefix (a wrong hash).
 */
export const rejectMatchesStation = (r: PushReject, station: PushStationCredential): boolean =>
  r.hintWuId === station.stationId ||
  r.hintWindguruUid === station.stationId ||
  (station.prefix != null && r.tokenPrefix === station.prefix);

export const fmtAgo = (ts: number, now: number = Date.now()): string => {
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
};

const fmtCountdown = (ms: number): string => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const describeMatch = (m: CaptureMatch): string =>
  [m.tokenPrefix && `prefix ${m.tokenPrefix}`, m.ip && `IP ${m.ip}`, m.tokenSha256 && `token ${m.tokenSha256.slice(0, 12)}…`]
    .filter(Boolean)
    .join(" + ");

const btn = "rounded-md px-3 py-2 text-sm font-medium text-white";
const inputCls =
  "w-full rounded bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:outline-none focus:border-accent";

// --- capture dialog -----------------------------------------------------------------------

const CaptureDialog: React.FC<{
  reject: PushReject;
  onClose: () => void;
  onStarted: (rule: CaptureRule) => void;
}> = ({ reject, onClose, onStarted }) => {
  const [by, setBy] = useState<"prefix" | "ip">(reject.tokenPrefix ? "prefix" : "ip");
  const [minutes, setMinutes] = useState(15);
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const match: CaptureMatch = by === "prefix" ? { tokenPrefix: reject.tokenPrefix as string } : { ip: reject.ip };
      const { rule } = await PushStationService.startCapture(match, minutes);
      toast.success(`Capturing rejected pushes for ${minutes} min`);
      onStarted(rule);
    } catch (err) {
      toast.error(getErrorMessage(err, "starting capture"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <form
        onSubmit={submit}
        aria-label="Capture next requests"
        className="w-full max-w-lg space-y-4 rounded-lg bg-gray-800 p-6 shadow-xl"
      >
        <h2 className="text-lg font-semibold text-white">Capture next rejected requests</h2>
        <div className="rounded border border-amber-700 bg-amber-900/30 p-3 text-sm text-amber-200">
          The edge stores up to 10 full rejected requests that match — including the token the device
          sends. Captures stay masked until you reveal one, expire 24 h after the capture window, and
          can be purged at any time. Replaces any capture already running.
        </div>
        <fieldset>
          <legend className="mb-1 text-sm text-white75">Match by</legend>
          <div className="flex flex-col gap-1 text-sm text-white">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="by"
                checked={by === "prefix"}
                disabled={!reject.tokenPrefix}
                onChange={() => setBy("prefix")}
              />
              Token prefix <span className="font-mono">{reject.tokenPrefix ?? "— (none)"}</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="by" checked={by === "ip"} onChange={() => setBy("ip")} />
              IP <span className="font-mono">{reject.ip}</span>
            </label>
          </div>
        </fieldset>
        <label className="block text-sm text-white75">
          Capture for
          <select className={`${inputCls} mt-1`} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>60 minutes</option>
          </select>
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded px-4 py-2 text-white75 hover:text-white">
            Cancel
          </button>
          <button type="submit" disabled={saving} className={`${btn} bg-accent disabled:opacity-50`}>
            {saving ? "Starting…" : "Start capture"}
          </button>
        </div>
      </form>
    </div>
  );
};

// --- adopt dialog -------------------------------------------------------------------------

const AdoptDialog: React.FC<{
  capture: PushCapture & { token: string };
  stations: PushStationCredential[];
  hint: string | null;
  onClose: () => void;
  onAdopted: () => void;
}> = ({ capture, stations, hint, onClose, onAdopted }) => {
  const candidates = useMemo(
    () =>
      stations
        .filter((s) => s.prefix && s.status !== "revoked")
        .sort((a, b) => a.stationId.localeCompare(b.stationId)),
    [stations]
  );
  const [stationId, setStationId] = useState<string>(
    candidates.find((s) => s.stationId === hint)?.stationId ?? ""
  );
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const target = candidates.find((s) => s.stationId === stationId) ?? null;

  const adopt = async () => {
    if (!target?.prefix) return;
    setConfirming(false);
    setSaving(true);
    try {
      await PushStationService.replaceCredential(target.prefix, capture.token, { verbatim: true });
      toast.success(`${target.stationId} now accepts the captured token`);
      onAdopted();
    } catch (err) {
      toast.error(getErrorMessage(err, "adopting the captured token"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div role="dialog" aria-label="Use as credential" className="w-full max-w-lg space-y-4 rounded-lg bg-gray-800 p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-white">Use captured token as a station credential</h2>
        <p className="text-sm text-gray-300">
          Token <span className="font-mono">{capture.tokenPrefix ?? capture.token.slice(0, 4)}…</span> from{" "}
          {capture.protocol} push, {fmtAgo(capture.ts)}. It is stored exactly as captured.
        </p>
        <label className="block text-sm text-white75">
          Station
          <select
            aria-label="Station"
            className={`${inputCls} mt-1`}
            value={stationId}
            onChange={(e) => setStationId(e.target.value)}
          >
            <option value="">Choose a push station…</option>
            {candidates.map((s) => (
              <option key={s.stationId} value={s.stationId}>
                {s.stationId}
                {s.name && s.name !== s.stationId ? ` — ${s.name}` : ""}
              </option>
            ))}
          </select>
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded px-4 py-2 text-white75 hover:text-white">
            Cancel
          </button>
          <button
            type="button"
            disabled={!target || saving}
            onClick={() => setConfirming(true)}
            className={`${btn} bg-blue-600 hover:bg-blue-500 disabled:opacity-50`}
          >
            {saving ? "Saving…" : "Use as credential"}
          </button>
        </div>
      </div>
      <ConfirmDialog
        isOpen={confirming}
        type="warning"
        title="Replace the station's credential?"
        message={`${target?.stationId ?? ""} will accept the captured token from now on. Its current token (prefix ${
          target?.prefix ?? "?"
        }) stops working after the usual 5-minute grace.`}
        confirmText="Replace credential"
        onConfirm={adopt}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
};

// --- captures panel -----------------------------------------------------------------------

const CapturesPanel: React.FC<{
  rule: CaptureRule | null;
  now: number;
  stations: PushStationCredential[];
  hintFor: (c: PushCapture) => string | null;
  onChanged: () => void;
}> = ({ rule, now, stations, hintFor, onChanged }) => {
  const [captures, setCaptures] = useState<PushCapture[]>([]);
  const [loading, setLoading] = useState(false);
  const [adopting, setAdopting] = useState<(PushCapture & { token: string }) | null>(null);
  const [adopted, setAdopted] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCaptures((await PushStationService.fetchCaptures()).captures);
    } catch (err) {
      toast.error(getErrorMessage(err, "loading captures"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, rule?.taken, rule?.createdAt]);

  const reveal = async (id: number) => {
    try {
      const res = await PushStationService.fetchCaptures(id);
      const raw = res.captures.find((c) => c.id === id);
      if (!raw) {
        toast.error("Capture no longer exists");
        return;
      }
      // Only the one revealed capture holds raw values; the rest stay masked.
      setCaptures((prev) => prev.map((c) => (c.id === id ? raw : c)));
    } catch (err) {
      toast.error(getErrorMessage(err, "revealing capture"));
    }
  };

  // Re-fetching the list drops the raw values: every capture comes back masked.
  const hide = () => load();

  const purge = async () => {
    setConfirmPurge(false);
    try {
      const { deleted } = await PushStationService.purgeCaptures();
      toast.success(`Purged ${deleted} capture${deleted === 1 ? "" : "s"}`);
      setCaptures([]);
      setAdopted(false);
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err, "purging captures"));
    }
  };

  return (
    <section aria-label="Captures" className="mb-4 rounded-lg border border-gray-700 bg-gray-800/40 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-gray-200">
          {rule ? (
            <span data-testid="active-rule">
              Capturing <span className="font-mono">{describeMatch(rule.match)}</span> —{" "}
              {rule.taken}/{rule.maxCaptures} captured, ends in{" "}
              <span className="tabular-nums">{fmtCountdown(rule.endsAt - now)}</span>
            </span>
          ) : (
            <span className="text-gray-400">No capture running.</span>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={load} disabled={loading} className={`${btn} bg-gray-700 hover:bg-gray-600 disabled:opacity-50`}>
            Refresh captures
          </button>
          {(captures.length > 0 || rule) && (
            <button type="button" onClick={() => setConfirmPurge(true)} className={`${btn} bg-red-700 hover:bg-red-600`}>
              Purge captures
            </button>
          )}
        </div>
      </div>
      {adopted && (
        <div role="status" className="mb-2 rounded border border-green-700 bg-green-900/30 p-2 text-sm text-green-200">
          Credential replaced. The captures still hold the token —{" "}
          <button type="button" onClick={() => setConfirmPurge(true)} className="underline">
            purge them now
          </button>
          .
        </div>
      )}
      {captures.length === 0 ? (
        <p className="text-sm text-gray-400">{loading ? "Loading captures…" : "No captured requests."}</p>
      ) : (
        <ul className="space-y-2">
          {captures.map((c) => (
            <li key={c.id} data-testid={`capture-${c.id}`} className="rounded border border-gray-700 bg-gray-900/60 p-2 text-xs">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-gray-300">
                <span>
                  <span className="font-mono text-white">
                    {c.method} {c.path}
                  </span>{" "}
                  · {c.protocol} · {c.ip} · {fmtAgo(c.ts, now)} · {c.reason}
                </span>
                <span className="flex gap-2">
                  {c.revealed ? (
                    <>
                      <button type="button" onClick={hide} className="text-cyan-400 hover:text-cyan-300">
                        Hide
                      </button>
                      {c.token && (
                        <button
                          type="button"
                          onClick={() => setAdopting({ ...c, token: c.token as string })}
                          className="text-green-300 hover:text-green-200"
                        >
                          Use as credential for station…
                        </button>
                      )}
                    </>
                  ) : (
                    <button type="button" onClick={() => reveal(c.id)} className="text-amber-300 hover:text-amber-200">
                      Reveal
                    </button>
                  )}
                </span>
              </div>
              <dl className="grid grid-cols-[8rem_1fr] gap-x-2 font-mono text-gray-200">
                {Object.entries(c.headers).map(([k, v]) => (
                  <React.Fragment key={k}>
                    <dt className="text-gray-400">{k}</dt>
                    <dd className="break-all">{v}</dd>
                  </React.Fragment>
                ))}
                {c.query && (
                  <>
                    <dt className="text-gray-400">query</dt>
                    <dd className="break-all">{c.query}</dd>
                  </>
                )}
                {c.body && (
                  <>
                    <dt className="text-gray-400">body</dt>
                    <dd className="break-all">{c.body}</dd>
                  </>
                )}
                {c.revealed && c.token && (
                  <>
                    <dt className="text-gray-400">token</dt>
                    <dd className="break-all text-green-200">{c.token}</dd>
                  </>
                )}
              </dl>
            </li>
          ))}
        </ul>
      )}
      {adopting && (
        <AdoptDialog
          capture={adopting}
          stations={stations}
          hint={hintFor(adopting)}
          onClose={() => setAdopting(null)}
          onAdopted={() => {
            setAdopting(null);
            setAdopted(true);
            onChanged();
          }}
        />
      )}
      <ConfirmDialog
        isOpen={confirmPurge}
        type="danger"
        title="Purge all captures?"
        message="Deletes every captured request (and the tokens in them) and stops the running capture."
        confirmText="Purge"
        onConfirm={purge}
        onCancel={() => setConfirmPurge(false)}
      />
    </section>
  );
};

// --- the view -----------------------------------------------------------------------------

const RejectedPushesView: React.FC<{
  stations: PushStationCredential[];
  /** Called after a credential was adopted, so the page reloads its station list. */
  onStationsChanged: () => void;
}> = ({ stations, onStationsChanged }) => {
  const [rejects, setRejects] = useState<PushReject[]>([]);
  const [rule, setRule] = useState<CaptureRule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [captureFor, setCaptureFor] = useState<PushReject | null>(null);
  const [now, setNow] = useState(Date.now());

  const table = useTableState<Filters>({
    defaultFilters: FILTERS,
    defaultSort: { key: "lastSeen", dir: "desc" },
    defaultPageSize: 25,
    pageSizes: PAGE_SIZES,
    descFirst: ["lastSeen", "count"],
    paramPrefix: REJECTS_PARAM_PREFIX,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await PushStationService.fetchRejects();
      setRejects(res.rejects);
      setRule(res.rule);
    } catch (err) {
      setError(getErrorMessage(err, "loading rejected pushes"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Countdown tick only while a capture runs; the rule disappears once it ends.
  useEffect(() => {
    if (!rule) return undefined;
    const t = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n >= rule.endsAt) setRule(null);
    }, 1000);
    return () => clearInterval(t);
  }, [rule]);

  const protocols = useMemo(() => Array.from(new Set(rejects.map((r) => r.protocol))).sort(), [rejects]);
  const { filters, setFilter, query, sort } = table;
  const filtered = useMemo(
    () =>
      rejects.filter((r) => {
        if (filters.protocol !== "all" && r.protocol !== filters.protocol) return false;
        if (!query) return true;
        return [r.tokenPrefix, r.ip, r.country, r.userAgent, r.path, r.reason, r.hintWuId, r.hintWindguruUid].some((v) =>
          (v || "").toLowerCase().includes(query)
        );
      }),
    [rejects, filters, query]
  );
  const sorted = useMemo(() => sortRows(filtered, sort, ACCESSORS), [filtered, sort]);
  const { pageRows, page, totalPages, total } = paginate(sorted, table.page, table.pageSize);

  /** Station hint for a capture: the aggregate row with the same token hash / IP. */
  const hintFor = useCallback(
    (c: PushCapture) => {
      const row =
        rejects.find((r) => r.tokenSha256 && r.tokenSha256 === c.tokenSha256 && stationHintOf(r)) ??
        rejects.find((r) => r.ip === c.ip && stationHintOf(r));
      return row ? stationHintOf(row) : null;
    },
    [rejects]
  );

  const colSpan = 8;
  return (
    <div>
      <div className="mb-3 rounded-lg border border-gray-700 bg-gray-800/40 p-3 text-sm text-gray-300">
        Credential rejects at the live-wind edge over the last 7 days, one row per protocol, path,
        token and IP. Use <span className="text-white">Capture next requests…</span> to read the token
        a device sends, then adopt it as that station&apos;s credential.
      </div>

      <CapturesPanel
        rule={rule}
        now={now}
        stations={stations}
        hintFor={hintFor}
        onChanged={() => {
          load();
          onStationsChanged();
        }}
      />

      <div className="mb-3 grid grid-cols-2 gap-3 rounded-lg border border-gray-700 bg-gray-800/40 p-3 md:grid-cols-4">
        <SearchInput
          className="col-span-2"
          label="Search rejects"
          value={table.queryInput}
          onChange={table.setQueryInput}
          placeholder="Prefix, IP, user agent, station hint…"
        />
        <FilterSelect
          label="Protocol"
          value={filters.protocol}
          onChange={(v) => setFilter("protocol", v)}
          options={[{ value: "all", label: "All" }, ...protocols.map((p) => ({ value: p, label: p }))]}
        />
        <div className="flex items-end justify-end">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className={`${btn} inline-flex items-center gap-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50`}
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            Refresh rejects
          </button>
        </div>
      </div>

      <div className="mb-2 text-sm text-gray-300" aria-live="polite">
        {loading ? "Loading rejected pushes…" : `${total} of ${rejects.length} reject groups`}
      </div>
      {error && (
        <div role="alert" className="mb-4 rounded border border-red-700 bg-red-900/40 p-3 text-red-200">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="min-w-full divide-y divide-gray-700 bg-gray-800/60 text-sm" aria-label="Rejected pushes">
          <thead className="bg-gray-900">
            <tr>
              <SortableHeader label="Last seen" sortKey="lastSeen" sort={sort} onSort={table.toggleSort} />
              <SortableHeader label="Protocol" sortKey="protocol" sort={sort} onSort={table.toggleSort} />
              <SortableHeader label="Token prefix" sortKey="prefix" sort={sort} onSort={table.toggleSort} />
              <SortableHeader label="IP" sortKey="ip" sort={sort} onSort={table.toggleSort} />
              <SortableHeader label="User agent" sortKey="ua" sort={sort} onSort={table.toggleSort} />
              <SortableHeader label="Count" sortKey="count" sort={sort} onSort={table.toggleSort} />
              <SortableHeader label="Station hint" sortKey="hint" sort={sort} onSort={table.toggleSort} />
              <th scope="col" className="px-3 py-2.5">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/70 text-gray-200">
            {loading ? (
              <TableSkeleton cols={colSpan} rows={4} />
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-gray-400">
                  {rejects.length === 0 ? "No credential rejects in the last 7 days." : "No rejects match the filters."}
                </td>
              </tr>
            ) : (
              pageRows.map((r) => (
                <tr key={`${r.protocol}|${r.path}|${r.tokenSha256 ?? (r.overflow ? "overflow" : "none")}|${r.ip}`}>
                  <td className="whitespace-nowrap px-3 py-2.5" title={new Date(r.lastSeen).toISOString()}>
                    {fmtAgo(r.lastSeen, now)}
                  </td>
                  <td className="px-3 py-2.5">
                    <div>{r.protocol}</div>
                    <div className="font-mono text-xs text-gray-400">{r.path}</div>
                  </td>
                  <td className="px-3 py-2.5 font-mono">
                    {r.overflow ? <span className="text-amber-300">many tokens</span> : r.tokenPrefix ?? <span className="text-gray-500">none</span>}
                    <div className="font-sans text-xs text-gray-400">{r.reason}</div>
                  </td>
                  <td className="px-3 py-2.5 font-mono">
                    {r.ip}
                    {r.country && <span className="ml-1 font-sans text-xs text-gray-400">{r.country}</span>}
                  </td>
                  <td className="max-w-[14rem] truncate px-3 py-2.5 text-xs" title={r.userAgent ?? ""}>
                    {r.userAgent ?? "—"}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums">{r.count}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{stationHintOf(r) ?? "—"}</td>
                  <td className="px-3 py-2.5 text-right">
                    <ActionsMenu
                      label={`Actions for reject from ${r.ip}`}
                      items={[{ label: "Capture next requests…", onSelect: () => setCaptureFor(r) }]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && total > 0 && (
        <Pagination
          darkMode
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          limit={table.pageSize}
          limitOptions={PAGE_SIZES}
          onPageChange={table.setPage}
          onLimitChange={table.setPageSize}
        />
      )}

      {captureFor && (
        <CaptureDialog
          reject={captureFor}
          onClose={() => setCaptureFor(null)}
          onStarted={(r) => {
            setCaptureFor(null);
            setRule(r);
            setNow(Date.now());
          }}
        />
      )}
    </div>
  );
};

export default RejectedPushesView;
